import axios from 'axios';
import { attachCsrfToRequestConfig, syncMetaCsrfToken, buildCsrfHeaders } from './Utils/csrf';
import toast from './Utils/toastManager';

const originalFetch = (typeof window !== 'undefined' && typeof window.fetch === 'function')
	? window.fetch.bind(window)
	: null;

const applyCsrfToAxiosDefaults = () => {
	if (typeof window === 'undefined' || !window.axios) return;
	const headers = buildCsrfHeaders();
	Object.entries(headers).forEach(([key, value]) => {
		if (value) {
			window.axios.defaults.headers.common[key] = value;
		}
	});
};

window.axios = axios;

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios.defaults.withCredentials = true;
applyCsrfToAxiosDefaults();

// Helper: attempt to refresh CSRF cookie (Laravel Sanctum / standard) then update axios header.
async function attemptCsrfRefresh() {
	try {
		// Standard endpoint (if Sanctum installed) else fallback to HEAD on root
		const url = '/sanctum/csrf-cookie';
		const fetchFn = originalFetch || fetch;
		const resp = await fetchFn(url, { credentials: 'same-origin' });
		if (!resp.ok) return false;
		syncMetaCsrfToken();
        applyCsrfToAxiosDefaults();
		return true;
	} catch (e) {
		return false;
	}
}

let csrfRetryInProgress = false;

async function handleCsrfMismatch() {
	if (csrfRetryInProgress) return; // avoid storms
	csrfRetryInProgress = true;
	// First silent try to refresh token
	const refreshed = await attemptCsrfRefresh();
	if (refreshed) {
		syncMetaCsrfToken();
		csrfRetryInProgress = false;
		return; // let caller retry original request manually (user action) – we don't auto‑replay to avoid side effects
	}
	// Fallback: inform user and full reload
	alert('Sesión caducada. Recargando para restablecer conexión segura.');
	window.location.reload();
}

window.axios.interceptors.request.use(
	(config) => attachCsrfToRequestConfig(config),
	(error) => Promise.reject(error),
);

// Axios interceptor: unify 419 handling
window.axios.interceptors.response.use(
	(response) => {
		try {
			const data = response?.data;
			// If backend returns a success message string, show it
			if (data && typeof data === 'object' && data.success === true && data.message) {
				toast.success(data.message);
			}
		} catch (e) {
			// ignore toast errors
		}

		return response;
	},
	async (error) => {
		try {
			const status = error?.response?.status;

			// Show simple toasts for common statuses
			if (status >= 500) {
				toast.error('Error del servidor. Inténtalo más tarde.');
			} else if (status === 422) {
				// Validation errors: try to extract message
				const data = error?.response?.data;
				const msg = (data && (data.message || (typeof data === 'string' ? data : null))) || 'Hay errores en el formulario.';
				toast.error(msg);
			} else if (status === 401) {
				toast.info('No autorizado. Por favor inicia sesión.');
			}

			if (status === 419) {
				const originalConfig = error?.config;
				if (originalConfig && !originalConfig.__isCsrfRetry) {
					const refreshed = await attemptCsrfRefresh();
					if (refreshed) {
						applyCsrfToAxiosDefaults();
						const retryConfig = attachCsrfToRequestConfig({
							...originalConfig,
							__isCsrfRetry: true,
						});
						return window.axios(retryConfig);
					}
				}
				await handleCsrfMismatch();
				applyCsrfToAxiosDefaults();
			}
		} catch (e) {
			console.error('Error en interceptor 419:', e);
		}

		return Promise.reject(error);
	},
);

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const shouldAttachCsrf = (method) => !SAFE_METHODS.has((method || 'GET').toUpperCase());

const applyCommonHeaders = (headers) => {
	headers.set('X-Requested-With', 'XMLHttpRequest');
};

const applyCsrfHeaders = (headers) => {
	const csrfHeaders = buildCsrfHeaders();
	Object.entries(csrfHeaders).forEach(([key, value]) => {
		if (value) {
			headers.set(key, value);
		}
	});
};

const buildCsrfAwareRequest = (input, init = {}) => {
	const baseRequest = input instanceof Request ? input : new Request(input, init);
	const method = (init.method || baseRequest.method || 'GET').toUpperCase();
	const headers = new Headers(baseRequest.headers || {});
	applyCommonHeaders(headers);
	if (shouldAttachCsrf(method)) {
		applyCsrfHeaders(headers);
		if (!headers.has('Accept')) {
			headers.set('Accept', 'application/json');
		}
	}
	const credentials = init.credentials ?? baseRequest.credentials ?? 'same-origin';
	return new Request(baseRequest, {
		...init,
		headers,
		credentials,
	});
};

const executeFetchWithRetry = async (request, retried = false) => {
	if (!originalFetch) {
		return fetch(request);
	}
	const requestForRetry = request.clone();
	const response = await originalFetch(request);
	// Attempt to show toast messages based on response payload/status
	try {
		const cloned = response.clone();
		const data = await cloned.json().catch(() => null);
		if (response.ok) {
			if (data && typeof data === 'object' && data.success === true && data.message) {
				toast.success(data.message);
			}
		} else {
			if (response.status >= 500) {
				toast.error('Error del servidor. Inténtalo más tarde.');
			} else if (response.status === 422) {
				const msg = (data && (data.message || (typeof data === 'string' ? data : null))) || 'Hay errores en el formulario.';
				toast.error(msg);
			} else if (response.status === 401) {
				toast.info('No autorizado. Por favor inicia sesión.');
			}
		}
	} catch (e) {
		// ignore parse/toast errors
	}
	if (response.status === 419) {
		if (!retried) {
			const refreshed = await attemptCsrfRefresh();
			if (refreshed) {
				const latestHeaders = new Headers(requestForRetry.headers || {});
				applyCsrfHeaders(latestHeaders);
				const retryRequest = new Request(requestForRetry, { headers: latestHeaders });
				return executeFetchWithRetry(retryRequest, true);
			}
		}
		await handleCsrfMismatch();
	}
	return response;
};

if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.fetch.__isCsrfWrapped) {
	const csrfAwareFetch = (input, init = {}) => {
		const request = buildCsrfAwareRequest(input, init);
		return executeFetchWithRetry(request, false);
	};
	Object.defineProperty(csrfAwareFetch, '__isCsrfWrapped', {
		value: true,
		enumerable: false,
		configurable: false,
	});
	window.fetch = csrfAwareFetch;
}

// Optional: Inertia global listener (defensive) - show toasts from Inertia responses/flash
try {
	window.addEventListener('inertia:error', async (evt) => {
		const st = evt?.detail?.status || evt?.detail?.response?.status;
		try {
			const respData = evt?.detail?.response?.data;
			const msg = respData?.message || respData?.errors || respData || null;
			if (msg) {
				const text = typeof msg === 'string' ? msg : JSON.stringify(msg);
				toast.error(text);
			}
		} catch (_) {}

		if (st === 419) {
			await handleCsrfMismatch();
		}
		applyCsrfToAxiosDefaults();
	});

	window.addEventListener('inertia:finish', (evt) => {
		syncMetaCsrfToken();
		applyCsrfToAxiosDefaults();

		try {
			const page = evt?.detail?.page || null;
			const flash = page?.props?.flash || page?.props?.flashMessage || null;
			if (flash) {
				if (typeof flash === 'string') {
					toast.info(flash);
				} else if (typeof flash === 'object') {
					if (flash.success) toast.success(flash.success);
					if (flash.error) toast.error(flash.error);
					if (flash.info) toast.info(flash.info);
					if (flash.message && !flash.success && !flash.error) toast.info(flash.message);
				}
			}
		} catch (_) {
			// ignore flash parsing errors
		}
	});
} catch (_) { /* noop */ }

try {
	window.addEventListener('focus', () => {
		syncMetaCsrfToken();
        applyCsrfToAxiosDefaults();
	});
} catch (_) { /* noop */ }

syncMetaCsrfToken();
applyCsrfToAxiosDefaults();
